import { action, computed, makeObservable, observable, override, runInAction } from "mobx";
import { BaseHomePreviewViewStore } from "application/home/stores";
import { UpdateNakiNigunAlbumStatusOptions, UpdateNakiNigunArtistStatusOptions } from "application/common/models";
import { findIndex, isNil } from "lodash";

class NakiNigunArtistViewStore extends BaseHomePreviewViewStore {
    @observable.ref albumStatusItems = [];

    @override
    get isTurnedOn() {
        if (!isNil(this.item)) {
            return this.item.isTurnedOn;
        }
        return false;
    }

    @computed get coverImageUrl() {
        if (this.item && this.item.coverImageId) {
            return this.rootStore.coverImageFactory.createNakiNigunArtistCoverImageUrl(
                this.item.coverImageId,
                200,
                200
            );
        }

        return null;
    }

    constructor(rootStore, { routeStore }) {
        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            id: id,
            navigateBack: "master.application.naki-nigun.list",
        });
        makeObservable(this);
        this.routeStore = routeStore;

        this.reaction(
            () => this.rootStore.userDeviceStore.selectedDevice,
            () => {
                this.onInit();
            }
        );
    }

    async get(id) {
        if (this.rootStore.userDeviceStore.selectedDevice) {
            return this.routeStore.getNakiNigunArtistWthDeviceSettings(
                id,
                this.rootStore.userDeviceStore.selectedDevice.id
            );
        }
        return this.routeStore.getNakiNigunArtist(id);
    }

    @action.bound
    async updateTurnedOnStatus(isTurnedOn) {
        try {
            this.setUpdatingStatus(true);
            const options = new UpdateNakiNigunArtistStatusOptions(
                this.item.id,
                this.item.name,
                this.item.isPremium,
                this.item.isVisibleInApp === false,
                this.item.isVisibleInEmbeddedApp === false
            );
            await this.rootStore.userContentManagerStore.updateNakiNigunArtistOnlyStatus(options, isTurnedOn);
            runInAction(() => {
                this.onInit();
            });
        } catch (err) {
            this.rootStore.notificationStore.error("Unexpected error occurred. Please contact the support team.", err);
        } finally {
            this.setUpdatingStatus(false);
        }
    }

    @action.bound
    async updateAlbumTurnedOnStatus(albumId, isTurnedOn) {
        try {
            this.statusToggleLoader.suspend();
            const albumIndex = findIndex(this.item.albums, (album) => album.id === albumId);
            const options = new UpdateNakiNigunAlbumStatusOptions(
                albumId,
                this.item.albums[albumIndex].title,
                this.item.albums[albumIndex].isPremium,
                this.item.albums[albumIndex].isVisibleInApp === false,
                this.item.albums[albumIndex].isVisibleInEmbeddedApp === false
            );
            await this.rootStore.userContentManagerStore.updateNakiNigunAlbumsStatus(options, isTurnedOn);
            runInAction(() => {
                if (this.item.albums[albumIndex].userDeviceAlbum) {
                    this.item.albums[albumIndex].userDeviceAlbum.isTurnedOn = isTurnedOn;
                } else {
                    this.item.albums[albumIndex].userDeviceAlbum = { isTurnedOn: isTurnedOn };
                }
            });
        } catch (err) {
            this.rootStore.notificationStore.error("Unexpected error occurred. Please contact the support team.", err);
        } finally {
            this.statusToggleLoader.resume();
        }
    }

    getStatusItem(deviceId, resourceId) {
        return this.routeStore.getUserDeviceAlbumsByArtist(deviceId, resourceId);
    }

    getStatusItemResource(deviceId, stationId) {
        // onInit() handles device-specific data through the full artist fetch.
        // Running getStatusItemResource in parallel would race against setItem().
        return Promise.resolve();
    }

    @override
    setStatusItem(statusItem) {
        this.statusItem = statusItem ? { isTurnedOn: statusItem.isTurnedOn } : null;
        this.albumStatusItems = (statusItem && statusItem.albums) || [];
    }

    evaluateContentAccess(item) {
        if (isNil(item)) {
            return false;
        }
        return this.rootStore.contentAccessValidator.canAccessNakiNigun({
            isPremium: item.isPremium,
            unavailableInMobileApp: item.isVisibleInApp === false,
            unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
        });
    }
}

export default NakiNigunArtistViewStore;
